import {
  StateMachineResponse,
  LoginWithIdpConnectionRequest,
  TenantType,
} from '@wix/ambassador-iam-authentication-v1-authentication/types';
import { loginWithIdpConnection } from '@wix/ambassador-iam-authentication-v1-authentication/http';
import { WIX_TENANT_ID } from './constants';

export interface OpenIdpPopupOptions {
  idpConnectionId: string;
  ldSessionId: string;
}

/**
 * Opens a popup window to initiate IDP (Identity Provider) login flow.
 * Uses BroadcastChannel and AES-GCM encryption for secure communication
 * between the popup and the parent window.
 *
 * @param options - Configuration options for the IDP popup
 * @returns Promise resolving to the IAM platform login response
 */
export const openIdpPopup = async (
  options: OpenIdpPopupOptions,
): Promise<StateMachineResponse> => {
  const { idpConnectionId, ldSessionId } = options;

  // Build request using Ambassador types for type-safety
  const request: Omit<
    LoginWithIdpConnectionRequest,
    'sessionId' | 'encryptionKey'
  > = {
    idpConnectionId,
    tenantId: WIX_TENANT_ID,
    tenantType: TenantType.ACCOUNT,
    customPayload: {
      ldSessionId,
    },
  };

  return openPopupWithRequest(request);
};

const openPopupWithRequest = async (
  request: Omit<LoginWithIdpConnectionRequest, 'sessionId' | 'encryptionKey'>,
): Promise<StateMachineResponse> => {
  // Open synchronously before any awaits — iOS Safari blocks window.open()
  // calls made after an await because the user-gesture context is lost.
  const popup = window.open(
    'about:blank',
    'oauthPopup',
    'width=450,height=522',
  );

  const sessionId = window.crypto.randomUUID();
  const key = await window.crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt'],
  );
  const encryptionKey = uint8ToBase64(
    new Uint8Array(await window.crypto.subtle.exportKey('raw', key)),
  );

  const url = buildLoginUrl({ ...request, sessionId, encryptionKey });

  return new Promise((resolve, reject) => {
    if (!popup) {
      reject(new Error('Popup was blocked'));
      return;
    }

    popup.location.href = url;

    const bc = new BroadcastChannel(`wix-idp-${sessionId}`);

    // Poll for popup closure — without this, a user dismissing the popup before
    // completing auth leaves the Promise pending forever and the channel open.
    const pollTimer = setInterval(() => {
      if (popup.closed) {
        clearInterval(pollTimer);
        bc.close();
        reject(new Error('Popup closed by user'));
      }
    }, 500);

    bc.addEventListener('message', async (event) => {
      clearInterval(pollTimer);

      const { data: eventData } = event;
      const { iv, data } = eventData;
      try {
        const decryptedData = await window.crypto.subtle.decrypt(
          { name: 'AES-GCM', iv },
          key,
          data,
        );
        const loginResponse = JSON.parse(
          new TextDecoder().decode(decryptedData),
        );
        if (loginResponse.error) {
          reject({
            error: loginResponse.error,
            redirectUrl: loginResponse.redirectUrl,
          });
        } else {
          resolve(JSON.parse(loginResponse.response));
        }
      } catch (err) {
        reject(err);
      }
      try {
        bc.postMessage(await encryptedCloseMessage(key));
      } catch {}
      bc.close();
    });
  });
};

const uint8ToBase64 = (arr: Uint8Array): string =>
  btoa(
    Array(arr.length)
      .fill('')
      .map((_, i) => String.fromCharCode(arr[i]))
      .join(''),
  );

const encryptedCloseMessage = async (key: CryptoKey) => {
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const data = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    new TextEncoder().encode('close'),
  );
  return { iv, data };
};

const buildLoginUrl = (request: LoginWithIdpConnectionRequest): string => {
  // Use Ambassador to generate the request options
  const requestOptionsFactory = loginWithIdpConnection(request);
  const requestOptions = requestOptionsFactory({
    isSSR: false,
    host: window.location.host,
  });

  // Build the full URL from the request options
  const { url, params } = requestOptions;
  if (!url) {
    throw new Error('Failed to build IDP login URL');
  }
  const queryString = params?.toString() || '';
  return queryString ? `${url}?${queryString}` : url;
};
